import { useState, useEffect, useRef } from 'react';
import Drawer from '@mui/material/Drawer';
import CloseIcon from '@mui/icons-material/Close';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Typography } from '../../atoms/Typography/Typography';
import { ConfirmationDialog } from '../../molecules/ConfirmationDialog/ConfirmationDialog';
import { useTranslation } from 'react-i18next';
import { ClickAwayListener } from '@mui/base/ClickAwayListener';
import { Box, Grid } from '@mui/material';
import { BasicButton } from '../../atoms';
import { useAppSelector } from '../../../common/hooks';
import { PdfDownloadErrorBanner } from '../../molecules/PdfDownloadErrorBanner/PdfDownloadErrorBanner';

const StyledRequiredText = styled(Typography)(({ theme }) => ({
    fontStyle: 'normal',
    color: theme.palette.semantic.text.mutedLight,
    fontSize: '13px',
    lineHeight: '18px',
    fontWeight: 500
}));

export interface DrawerProps {
    open?: boolean;
    header?: string;
    subHeading?: string;
    buttonlabel?: string;
    secondaryButtonLabel?: string | boolean;
    // eslint-disable-next-line no-unused-vars
    handleClose: (flag: boolean) => void;
    children?: React.ReactNode | React.ReactNode[];
    sx?: object;
    onSubmit?: () => void;
    onSecondarySubmit?: () => void;
    error?: any;
    errorMessage?: string;
    resetChild?: () => void;
    loading?: boolean;
    disableSubmit?: boolean;
    isValid?: boolean;
    askConfirm?: boolean;
    drawerIcon?: React.ReactNode;
    hasChange?: boolean;
    heapId?: string;
    APIError?: boolean | number;
    showStyledRequiredText?: boolean;
    showFooter?: boolean;
}

const Footer = styled('footer')(({ theme }) => ({
    width: '100%',
    position: 'sticky',
    bottom: 0,
    marginBottom: 0,
    zIndex: 1,
    background: theme.palette.semantic.bg.default,
    borderTop: `1px solid ${theme.palette.semantic.bd.strong}`
}));

export const DrawerComp = ({
    open,
    header,
    subHeading,
    drawerIcon,
    buttonlabel,
    handleClose,
    children,
    error,
    onSubmit,
    onSecondarySubmit,
    errorMessage,
    resetChild,
    loading,
    disableSubmit,
    isValid,
    askConfirm = true,
    secondaryButtonLabel,
    hasChange,
    heapId,
    APIError,
    showStyledRequiredText = true,
    showFooter = true
}: DrawerProps) => {
    const toggleDrawer =
        (open: boolean) =>
        // eslint-disable-next-line no-unused-vars
        (event: React.KeyboardEvent | React.MouseEvent) => {
            if (askConfirm && hasChange) setDeleteModalState(!open);
            else handleClose(open);
        };

    const [deleteModalState, setDeleteModalState] = useState(false);
    const [showAPIError, setShowAPIError] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation();
    const theme = useTheme();

    const isXl = useMediaQuery(theme.breakpoints.up('xl'));
    const isLg = useMediaQuery(theme.breakpoints.up('lg'));
    const isMd = useMediaQuery(theme.breakpoints.up('md'));
    const isCookiePolicyAlertBoxClosed = useAppSelector((state) => state.global.isCookiePolicyAlertBoxClosed);

    useEffect(() => {
        if (APIError) setShowAPIError(true);
    }, [APIError]);

    useEffect(() => {
        const handleFieldChange = () => {
            if (showAPIError) setShowAPIError(false);
        };
        const el = contentRef.current;
        if (el && showAPIError) {
            el.addEventListener('input', handleFieldChange);
            el.addEventListener('change', handleFieldChange);
            return () => {
                el.removeEventListener('input', handleFieldChange);
                el.removeEventListener('change', handleFieldChange);
            };
        }
    }, [showAPIError]);

    const getResponsiveWidth = () => {
        if (isXl) return '30%';
        if (isLg || isMd) return '40%';
        return '50%'; // default
    };

    const handleLeave = () => {
        setDeleteModalState(!deleteModalState);
        setShowAPIError(false);
        handleClose(!open);
        if (resetChild) resetChild();
    };

    const handleStay = async () => {
        setDeleteModalState(!deleteModalState);
    };

    const styleSx = {
        '& .MuiPaper-root': {
            width: getResponsiveWidth()
        },
        'MuiBackdrop-root-MuiModal-backdrop': {
            backgroundColor: 'inherit'
        }
    };
    return (
        <>
            <ClickAwayListener onClickAway={handleLeave}>
                <>
                    {open && (
                        <Drawer
                            anchor={'right'}
                            open={open}
                            onClose={toggleDrawer(false)}
                            variant="temporary"
                            sx={styleSx}
                        >
                            {hasChange && (
                                <ConfirmationDialog
                                    onCancellation={handleLeave}
                                    onConfirmation={() => handleStay()}
                                    onCloseIconClick={() => handleStay()}
                                    open={deleteModalState}
                                    content={`${t('DRAWER_CLOSE_MODAL_HEADER_TEXT')}`}
                                    confirmButtonLabel={`${t('DRAWER_CLOSE_MODAL_HEADER_TEXT_STAY')}`}
                                    cancelButtonLabel={`${t('DRAWER_CLOSE_MODAL_HEADER_TEXT_LEAVE')}`}
                                    title={t('DRAWER_CLOSE_MODAL_TITLE')}
                                    width={getResponsiveWidth()}
                                />
                            )}
                            <Grid
                                container
                                gap="10px"
                                justifyContent="center"
                                padding="12px 16px"
                                alignItems="center"
                                borderBottom={`1px solid ${theme.palette.semantic.bd.strong}`}
                                sx={{
                                    backgroundColor: theme.palette.semantic.bg.default,
                                    zIndex: 100,
                                    top: 0,
                                    position: 'sticky'
                                }}
                            >
                                {drawerIcon && (
                                    <Grid
                                        container
                                        alignItems="center"
                                        justifyContent="center"
                                        item
                                        width="40px"
                                        height="40px"
                                        // padding="8px"
                                        borderRadius="50%"
                                        sx={{ background: theme.palette.semantic.bg.brandLight2, aspectRatio: 1 }}
                                    >
                                        {drawerIcon}
                                    </Grid>
                                )}
                                <Grid item flexGrow={1} display="flex" flexDirection="column">
                                    <Grid display="flex" flexDirection="row" gap={'8px'}>
                                        <Typography
                                            variant="headers.h4"
                                            label={header || ''}
                                            as="h4"
                                            sx={{ color: theme.palette.primary[800] }}
                                        />
                                        {showStyledRequiredText && (
                                            <StyledRequiredText>{t('DRAWER_HEADER_REQUIRED_LABEL')}</StyledRequiredText>
                                        )}
                                    </Grid>
                                    {subHeading && (
                                        <Typography
                                            color="semantic.text.default"
                                            variant="body.small"
                                            label={subHeading}
                                            sx={{ color: theme.palette.semantic.text.default }}
                                        />
                                    )}
                                </Grid>
                                <Grid item width="24px" height="24px">
                                    <CloseIcon
                                        sx={{ color: theme.palette.semantic.icon.dark }}
                                        onClick={toggleDrawer(false)}
                                    />
                                </Grid>
                            </Grid>

                            <Grid ref={contentRef} flexGrow={1} margin={2}>
                                {children}
                            </Grid>

                            {showFooter && (
                                <Footer
                                    sx={{
                                        padding: secondaryButtonLabel ? '16px' : '24px 16px',
                                        marginBottom: isCookiePolicyAlertBoxClosed ? 0 : '70px'
                                    }}
                                >
                                    {!loading && !!error?.response?.data?.message && (
                                        <Typography
                                            variant="body.large"
                                            label={errorMessage}
                                            sx={{
                                                fontStyle: 'italic',
                                                textAlign: 'center',
                                                marginBottom: 10,
                                                marginTop: -10,
                                                color: theme.palette.berry[400]
                                            }}
                                        />
                                    )}
                                    {!loading && showAPIError && (
                                        <Box marginBottom={2}>
                                            <PdfDownloadErrorBanner i18nKey="SAVE_PLAN_FAILED_BANNER" />
                                        </Box>
                                    )}
                                    <Grid
                                        alignItems="center"
                                        flexDirection="row"
                                        width="100%"
                                        gap="10px"
                                        display="flex"
                                    >
                                        {secondaryButtonLabel && (
                                            <BasicButton
                                                disabled={isValid}
                                                fullWidth={true}
                                                size="medium"
                                                color="primary"
                                                variant={isValid ? 'contained' : 'outlined'}
                                                onClick={() => {
                                                    setShowAPIError(false);
                                                    onSecondarySubmit?.();
                                                }}
                                                sx={{
                                                    textTransform: 'unset',
                                                    borderRadius: '100px',
                                                    '&:hover': {
                                                        backgroundColor: (theme) => theme.palette.primary[100],
                                                        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)'
                                                    }
                                                }}
                                            >
                                                {secondaryButtonLabel}
                                            </BasicButton>
                                        )}
                                        <BasicButton
                                            disabled={loading || disableSubmit}
                                            size="medium"
                                            color="secondary"
                                            type="submit"
                                            variant="contained"
                                            onClick={onSubmit}
                                            sx={{
                                                textTransform: 'unset',
                                                borderRadius: '100px'
                                            }}
                                            loading={loading}
                                            data-heap-id={heapId}
                                        >
                                            {buttonlabel || t('DRAWER_BUTTON_LABEL')}
                                        </BasicButton>
                                    </Grid>
                                </Footer>
                            )}
                        </Drawer>
                    )}
                </>
            </ClickAwayListener>
        </>
    );
};
